<template>
  <v-row>
    <CreateSequence v-model="isCreateSequenceDialogVisible"
                    @showNewSequenceDialog="showNewSequenceDialog()" />
    <DeleteItem :title="$t('general.doYouReallyWanToDeleteThisSequence')"
                v-model="isDeleteSequenceDialogVisible"
                @deleteItem="deleteSequence()"
                @input="clearSelectedSequenceId()" />
    <NewEditSequence v-model="isNewEditSequenceDialogVisible"
                     :method="method"
                     :selected-sequence-id="selectedSequenceId"
                     @getSequences="getSequences()" />
    <v-col cols="12" class="d-flex justify-end py-0">
      <v-btn color="mainButtons" dark @click="showCreateSequenceDialog()">
        {{ $t('general.newSequence') }}
      </v-btn>
    </v-col>

    <v-col cols="12" class="py-0">
      <v-data-table
        fixed-header
        :headers="tableHeaders"
        :height="dataTableHeight"
        :items="sequences"
        item-key="sequenceId"
        :loading="loadingSequences"
        :options.sync="options"
        show-select
        :footer-props="{
            'items-per-page-text': `${$t('general.sequences')} ${$t('general.perPage')}`,
            'items-per-page-options': [50, 100, 200]
          }"
        :server-items-length="totalSequences"
        class="elevation-2 mt-4"
        :must-sort="false"
        v-model="selectedSequences">
        <template v-slot:item="{ item }">
          <tr class="kbDarkBlueText--text">
            <td>
              <div>
                <v-checkbox color="kbBlue"
                            :value="item"
                            v-model="selectedSequences"/>
              </div>
            </td>

            <td>
              <div>
                <span class="kbDarkBlueText--text font-weight-bold text-capitalize">
                  {{ item.name }}
                </span>
              </div>
            </td>

            <td>
              <div class="text-center">
                <span class="kbDarkBlueText--text text-capitalize">
                  {{ convertTZToUserTZ(item.createdAt) | moment('MMMM Do YYYY') }}
                </span>
              </div>
            </td>

            <td>
              <div class="d-flex align-center justify-center">
                <v-icon :color="sequenceStatusColor(item.status)"
                        class="pr-2"
                        x-small>
                  mdi-circle
                </v-icon>
                <span class="kbDarkBlueText--text text-capitalize">
                  {{ item.status }}
                </span>
              </div>
            </td>

            <td>
              <div class="d-flex align-center">
                <span class="kbDarkBlueText--text mr-3">
                  {{ `${item.completion || 0}%` }}
                </span>
                <v-progress-linear
                  :value="item.completion"
                  :color="setCompletionColor(item.completion)"
                />
              </div>
            </td>

            <td>
              <div class="d-flex align-center justify-center">
                <v-switch color="kbBlue"
                          :input-value="item.active"
                          @change="changeSequenceStatus($event, item.id)" />
              </div>
            </td>

            <td>
              <div class="text-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn color="kbDarkBlueText"
                           icon
                           @click="goToSequence(item.id)"
                           v-on="on">
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('general.seeSequence') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn color="kbDarkBlueText"
                           class="ml-1"
                           icon
                           @click="showEditSequenceDialog(item.id)"
                           v-on="on">
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('general.editSequence') }}</span>
                </v-tooltip>

                <v-tooltip bottom v-if="administratorRoles.includes('ROLE_ADMIN')">
                  <template v-slot:activator="{ on }">
                    <v-btn color="kbRed"
                           class="ml-1"
                           icon
                           @click="showDeleteSequenceDialog(item.id)"
                           v-on="on">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('general.deleteSequence') }}</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </template>

        <template v-slot:no-data>
          <v-row>
            <v-col
              class="text-center"
              v-if="loadingSequences"
              cols="12"
            >{{ $t('general.loading') }} {{ $t('general.sequences') }}</v-col>
            <v-col
              class="red--text text-center"
              v-else
              cols="12"
            >{{ $t('general.noSequencesCreated') }}</v-col>
          </v-row>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'vue-moment'
import notifications from '@/libs/notifications'
import CreateSequence from '@/pages/App/Sequences/dialogs/CreateSequence'
import DeleteItem from '@/components/dialogs/DeleteItem'
import NewEditSequence from '@/pages/App/Sequences/dialogs/NewEditSequence'
import ModuleTableHeight from '@/mixins/ModuleTableHeight'
import TimeZones from '@/mixins/TimeZones'
export default {
  components: { CreateSequence, DeleteItem, NewEditSequence },
  computed: {
    ...mapGetters(['administratorRoles', 'appUuid', 'companyUuid']),
    ...mapState({
      sequences: state => state.sequences.sequences,
      totalSequences: state => state.sequences.totalSequences
    }),
    tableHeaders () {
      return [
        { text: this.$t('general.sequence'), value: 'project', align: 'left', sortable: true },
        { text: this.$t('general.creationDate'), value: 'createdAt', align: 'center', sortable: true },
        { text: this.$t('general.status'), value: 'step', align: 'center', sortable: true },
        { text: this.$t('general.completion'), value: 'completion', align: 'left', sortable: true },
        { text: this.$t('general.isSequenceActive'), value: 'active', align: 'center', sortable: true },
        { text: this.$t('general.actions'), value: '', align: 'center', sortable: false }
      ]
    }
  },
  data () {
    return {
      isCreateSequenceDialogVisible: false,
      isDeleteSequenceDialogVisible: false,
      isNewEditSequenceDialogVisible: false,
      loadingSequences: false,
      method: 'create',
      moment,
      options: {},
      selectedSequenceId: '',
      selectedSequences: []
    }
  },
  methods: {
    changeSequenceStatus (newValue, sequenceId) {
      !newValue ? this.stopSequence(sequenceId) : this.runSequence(sequenceId)
    },
    clearSelectedSequenceId () {
      this.selectedSequenceId = ''
    },
    clearSelectedSequences () {
      this.selectedSequences = []
    },
    async deleteSequence () {
      try {
        await this.$store.dispatch('deleteSequence',
          { appId: this.appUuid, sequenceId: this.selectedSequenceId }
        )
        notifications.success(this.$t('general.sequenceSuccessfullyDeleted'))
        await this.getSequences()
      } catch (e) {
        notifications.error(e)
      }
    },
    async getSequences () {
      try {
        this.loadingSequences = true
        await this.$store.dispatch('getSequences', { appId: this.$store.getters.appUuid, pagination: this.options })
        this.clearSelectedSequences()
        this.loadingSequences = false
      } catch (e) {
        notifications.error(e)
        this.loadingSequences = false
      }
    },
    goToSequence (sequenceId) {
      this.$router.push(
        {
          name: 'Sequence Leads',
          params: {
            appid: this.appUuid,
            sequenceId
          }
        }
      )
    },
    async runSequence (sequenceId) {
      try {
        await this.$store.dispatch('runSequence', { sequenceId })
        this.getSequences()
      } catch (e) {
        notifications.error(e)
      }
    },
    sequenceStatusColor (status) {
      switch (status) {
        case 'pending':
          return 'kbRed'
        case 'completed':
          return 'kbBlue'
        case 'onSchedule':
          return 'kbBlue'
        case 'delayed':
          return 'kbRed'
        default:
          return 'kbRed'
      }
    },
    setCompletionColor (value) {
      value = parseFloat(value)
      if (value <= 25) return 'rgb(255, 0, 0)'
      else if (value <= 50) return 'orange'
      else if (value <= 75) return 'yellow'
      else return 'kbGreen'
    },
    showCreateSequenceDialog () {
      this.isCreateSequenceDialogVisible = true
    },
    showDeleteSequenceDialog (sequenceId) {
      this.selectedSequenceId = sequenceId
      this.isDeleteSequenceDialogVisible = true
    },
    showEditSequenceDialog (sequenceId) {
      this.method = 'edit'
      this.selectedSequenceId = sequenceId
      this.isNewEditSequenceDialogVisible = true
    },
    showNewSequenceDialog () {
      this.method = 'create'
      this.selectedSequenceId = ''
      this.isNewEditSequenceDialogVisible = true
    },
    async stopSequence (sequenceId) {
      try {
        await this.$store.dispatch('stopSequence', { sequenceId })
        this.getSequences()
      } catch (e) {
        notifications.error(e)
      }
    }
  },
  mixins: [ModuleTableHeight, TimeZones],
  watch: {
    options: {
      handler () {
        this.getSequences()
      },
      deep: true
    }
  }
}
</script>
